import { useEffect, useMemo } from 'react';
import { useMap } from 'react-naver-maps';
import { useUserCarInfo } from '@/entities/car';
import {
	DirectionSchema,
	type OriginId,
	useDirectionDistances,
} from '@/entities/direction';
import { useStation as useStations } from '@/entities/station';
import {
	DEFAULT_FUEL_AMOUNT,
	DEFAULT_FUEL_EFFICIENCY,
	type FUEL_TYPE_OPTION,
} from '@/shared/consts';
import { useLocation } from '@/shared/lib';
import { createStationsWithSavings } from '../lib';
import useNaverMapStore from '../model/naverMapStore';

type NaverMapStationFetcherProps = {
	fuelType: (typeof FUEL_TYPE_OPTION)[number]['value'];
};

const NaverMapStationFetcher = ({ fuelType }: NaverMapStationFetcherProps) => {
	const map = useMap();
	const stations = useStations(fuelType);
	const setStationsWithSavings = useNaverMapStore(
		(s) => s.setStationsWithSavings,
	);
	const { carInfo } = useUserCarInfo();
	const { location } = useLocation();
	const mapCenter = map.getCenter();
	const originLat = location?.lat ?? mapCenter.y;
	const originLng = location?.lng ?? mapCenter.x;
	const originId = `${originLat},${originLng}` as OriginId;

	const directions = useMemo(
		() =>
			stations.map((station) =>
				DirectionSchema.parse({
					originId,
					destinationId: station.id,
					origin: { lat: originLat, lng: originLng },
					destination: { lat: station.coorY, lng: station.coorX },
				}),
			),
		[stations, originId, originLat, originLng],
	);

	const distanceMap = useDirectionDistances(directions);

	const stationsWithSavings = useMemo(
		() =>
			createStationsWithSavings({
				stations,
				originId,
				distanceMap,
				fuelAmount: carInfo?.average_fuel_amount ?? DEFAULT_FUEL_AMOUNT,
				fuelEfficiency: carInfo?.efficiency ?? DEFAULT_FUEL_EFFICIENCY,
			}),
		[
			stations,
			originId,
			distanceMap,
			carInfo?.average_fuel_amount,
			carInfo?.efficiency,
		],
	);

	useEffect(() => {
		setStationsWithSavings(stationsWithSavings);
	}, [stationsWithSavings, setStationsWithSavings]);

	return null;
};

export default NaverMapStationFetcher;
